import React, { useState, useEffect } from 'react';
import AdminLeftMenu from './backend/AdminLeftMenu';
import firebaseNotificationService from '../services/firebaseNotificationService';
import { useAuth } from '../contexts/AuthContext';

const Notifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { currentUser } = useAuth();

    useEffect(() => {
        const loadFirebaseNotifications = async () => {
            if (!currentUser?.uid) {
                console.log('🔔 No user logged in, skipping notification load');
                console.log('🔔 Current user object:', currentUser);
                setIsLoading(false);
                return;
            }

            try {
                console.log('🔔 Loading notifications from Firebase for user:', currentUser.uid);
                console.log('🔔 Current user full object:', currentUser);
                
                // Load existing notifications
                const existingNotifications = await firebaseNotificationService.getUserNotifications(currentUser.uid, 50);
                console.log('🔔 Loaded notifications from Firebase:', existingNotifications.length);
                
                // Convert Firebase notifications to display format
                const formattedNotifications = existingNotifications.map(notification => ({
                    id: notification.id,
                    type: notification.type,
                    title: notification.title,
                    message: notification.message,
                    practitioner_name: notification.practitioner_name,
                    data: notification.data || {},
                    timestamp: notification.createdAt?.toISOString() || notification.timestamp,
                    read: notification.isRead || false,
                    source: notification.source || 'Epic_FHIR',
                    requiresAction: notification.requiresAction || false,
                    status: notification.status || 'pending'
                }));

                setNotifications(formattedNotifications);
                
                // Set up real-time listener for new notifications
                const unsubscribe = firebaseNotificationService.subscribeToNotifications(
                    currentUser.uid, 
                    (newNotifications) => {
                        console.log('🔔 Real-time notification update:', newNotifications.length);
                        
                        const formatted = newNotifications.map(notification => ({
                            id: notification.id,
                            type: notification.type,
                            title: notification.title,
                            message: notification.message,
                            practitioner_name: notification.practitioner_name,
                            data: notification.data || {},
                            timestamp: notification.createdAt?.toISOString() || notification.timestamp,
                            read: notification.isRead || false,
                            source: notification.source || 'Epic_FHIR',
                            requiresAction: notification.requiresAction || false,
                            status: notification.status || 'pending'
                        }));
                        
                        setNotifications(formatted);
                    }
                );

                setIsLoading(false);
                
                return unsubscribe;
                
            } catch (error) {
                console.error('🔔 Error loading Firebase notifications:', error);
                setIsLoading(false);
            }
        };

        let unsubscribe;
        loadFirebaseNotifications().then(unsub => {
            unsubscribe = unsub;
        });

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [currentUser?.uid]);

    const markAsRead = async (notificationId) => {
        try {
            // Optimistically update UI
            setNotifications(prev => 
                prev.map(n => n.id === notificationId ? {...n, read: true} : n)
            );
            
            // Update in Firebase
            await firebaseNotificationService.markAsRead(notificationId);
            console.log('🔔 Marked notification as read:', notificationId);
        } catch (error) {
            console.error('🔔 Error marking notification as read:', error);
            // Revert optimistic update
            setNotifications(prev => 
                prev.map(n => n.id === notificationId ? {...n, read: false} : n)
            );
        }
    };

    const clearNotification = async (notificationId) => {
        try {
            // Optimistically update UI
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            
            // Delete from Firebase
            await firebaseNotificationService.deleteNotification(notificationId);
            console.log('🔔 Deleted notification:', notificationId);
        } catch (error) {
            console.error('🔔 Error deleting notification:', error);
            // Could reload notifications here if needed
        }
    };

    const clearAllNotifications = async () => {
        if (!currentUser?.uid) return;
        
        try {
            const currentNotifications = [...notifications];
            
            // Optimistically clear UI
            setNotifications([]);
            
            // Delete all notifications from Firebase
            const deletePromises = currentNotifications.map(notification => 
                firebaseNotificationService.deleteNotification(notification.id)
            );
            
            await Promise.all(deletePromises);
            console.log('🔔 Cleared all notifications from Firebase');
        } catch (error) {
            console.error('🔔 Error clearing all notifications:', error);
            // Could reload notifications here if needed
        }
    };

    const handleNotificationAction = async (notificationId, action) => {
        try {
            // Update notification status in Firebase
            await firebaseNotificationService.handleUserAction(notificationId, action);
            
            // Update UI
            setNotifications(prev => 
                prev.map(n => n.id === notificationId ? {...n, status: action, read: true} : n)
            );
            
            console.log(`🔔 Handled notification action: ${action} for ${notificationId}`);
        } catch (error) {
            console.error('🔔 Error handling notification action:', error);
        }
    };

    const [selectedNotification, setSelectedNotification] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const openNotificationModal = (notification) => {
        setSelectedNotification(notification);
        setShowModal(true);
    };

    const closeNotificationModal = () => {
        setSelectedNotification(null);
        setShowModal(false);
    };

    const renderNotification = (notification) => {
        const { type, title, message, data, timestamp, read, id, requiresAction, status, practitioner_name } = notification;

        const getNotificationIcon = (type) => {
            switch (type) {
                case 'APPOINTMENT_NEW': return '';
                case 'APPOINTMENT_RESCHEDULED': return '';
                case 'APPOINTMENT_CANCELLED': return '';
                case 'APPOINTMENT_STATUS_CHANGED': return '';
                default: return '';
            }
        };

        const formatDetailedTime = (timestamp) => {
            const date = new Date(timestamp);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMins / 60);

            // For recent notifications, show relative time
            if (diffMins < 1) return 'Just now';
            if (diffMins < 60) return `${diffMins} minutes ago`;
            if (diffHours < 24) return `${diffHours} hours ago`;
            
            // For older notifications, show formatted date
            const options = { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'long',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            };
            
            const formatter = new Intl.DateTimeFormat('en-US', options);
            const formatted = formatter.format(date);
            
            // Add ordinal suffix to day
            const day = date.getDate();
            const ordinal = day === 1 || day === 21 || day === 31 ? 'st' :
                          day === 2 || day === 22 ? 'nd' :
                          day === 3 || day === 23 ? 'rd' : 'th';
            
            return formatted.replace(day.toString(), `${day}${ordinal}`);
        };

        return (
            <div key={id} className="mb-3">
                <div className="notification-card" style={{
                    backgroundColor: read ? '#f8f9fa' : 'white',
                    border: '1px solid #e9ecef',
                    borderRadius: '12px',
                    padding: '16px',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    borderLeft: `4px solid ${read ? '#6c757d' : '#E07A55'}`
                }}>
                    {/* Close button */}
                    <button 
                        type="button" 
                        className="btn-close position-absolute" 
                        style={{ 
                            top: '12px', 
                            right: '12px',
                            fontSize: '10px',
                            opacity: 0.5
                        }}
                        onClick={() => clearNotification(id)}
                    ></button>
                    
                    {/* Main content */}
                    <div className="d-flex align-items-start">
                        {/* Content */}
                        <div className="flex-grow-1" style={{ paddingRight: '20px' }}>
                            {/* Title and practitioner */}
                            <div className="mb-1">
                                <h6 className="mb-0" style={{ 
                                    fontWeight: '600', 
                                    color: read ? '#6c757d' : '#333',
                                    fontSize: '14px',
                                    lineHeight: '1.3'
                                }}>
                                    {title}
                                    {practitioner_name && (
                                        <span style={{ 
                                            fontWeight: '500',
                                            color: '#E07A55',
                                            marginLeft: '8px'
                                        }}>
                                            • Dr. {practitioner_name}
                                        </span>
                                    )}
                                </h6>
                            </div>

                            {/* Appointment details in one line */}
                            {data && (data.start_date || data.start_time || data.procedure || data.location) && (
                                <div className="mb-2" style={{ 
                                    fontSize: '12px', 
                                    color: '#6c757d',
                                    lineHeight: '1.4'
                                }}>
                                    {data.start_date && (
                                        <span className="me-3">
                                            📅 {new Date(data.start_date).toLocaleDateString()}
                                        </span>
                                    )}
                                    {data.start_time && (
                                        <span className="me-3">
                                            ⏰ {data.start_time}
                                        </span>
                                    )}
                                    {data.procedure && (
                                        <span className="me-3">
                                            🩺 {data.procedure}
                                        </span>
                                    )}
                                    {data.location && (
                                        <span>
                                            🏥 {data.location}
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Time and actions row */}
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-2">
                                    {/* View Details button - Far left */}
                                    <button 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => openNotificationModal(notification)}
                                        style={{ 
                                            fontSize: '10px',
                                            padding: '4px 8px',
                                            borderRadius: '6px'
                                        }}
                                    >
                                        View Details
                                    </button>

                                    <small className="text-muted" style={{ fontSize: '11px' }}>
                                        {formatDetailedTime(timestamp)}
                                    </small>
                                </div>
                                
                                <div className="d-flex align-items-center gap-2">
                                    {/* Action Buttons for notifications requiring user action */}
                                    {requiresAction && status === 'pending' && (
                                        <>
                                            <button 
                                                className="btn btn-sm btn-success"
                                                onClick={() => handleNotificationAction(id, 'accepted')}
                                                style={{ 
                                                    fontSize: '10px',
                                                    padding: '4px 8px',
                                                    borderRadius: '6px'
                                                }}
                                            >
                                                Accept
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleNotificationAction(id, 'rejected')}
                                                style={{ 
                                                    fontSize: '10px',
                                                    padding: '4px 8px',
                                                    borderRadius: '6px'
                                                }}
                                            >
                                                Reject
                                            </button>
                                        </>
                                    )}

                                    {/* Mark as read button */}
                                    {!read && !requiresAction && (
                                        <button 
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => markAsRead(id)}
                                            style={{ 
                                                fontSize: '10px',
                                                padding: '4px 8px',
                                                borderRadius: '6px'
                                            }}
                                        >
                                            Mark as Read
                                        </button>
                                    )}

                                    {/* Status indicator for handled actions */}
                                    {status && status !== 'pending' && (
                                        <span className={`badge ${status === 'accepted' ? 'bg-success' : 'bg-danger'}`} 
                                              style={{ fontSize: '9px', padding: '2px 6px' }}>
                                            {status === 'accepted' ? 'Accepted' : 'Rejected'}
                                        </span>
                                    )}

                                    {/* Unread indicator */}
                                    {!read && (
                                        <div style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: '#E07A55'
                                        }}></div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="offcanvas offcanvas-start" id="demo">
                <AdminLeftMenu />
            </div>
            
            <div className="rightpane mynotif">
                <div className="container">
                    <div className='row justify-content-center'>
                        <div className='col-md-8 text-center'>
                            <img src="\images\notification-hero.png" width={'323'} alt='' /> 
                            <h2>My Notifications</h2>
                            
                            {notifications.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                                    <small className="text-muted">
                                        {notifications.filter(n => !n.read).length} unread of {notifications.length} total
                                    </small>
                                    <button 
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={clearAllNotifications}
                                    >
                                        Clear All
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className='col-12 col-md-8 mt-3'>
                            {isLoading ? (
                                <div className="text-center py-4">
                                    <div className="loading-spinner-container">
                                        <div className="loading-spinner"></div>
                                        <p>Loading notifications from Firebase...</p>
                                    </div>
                                </div>
                            ) : notifications.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="fa fa-bell-slash fa-3x text-muted mb-3"></i>
                                    <h5>No Notifications</h5>
                                    <p className="text-muted">
                                        You'll see real-time notifications here when:
                                    </p>
                                    <ul className="list-unstyled text-muted">
                                        <li>• New appointments are scheduled</li>
                                        <li>• Appointment times change</li>
                                        <li>• Appointments are cancelled</li>
                                        <li>• Status updates occur</li>
                                    </ul>
                                </div>
                            ) : (
                                notifications.map(renderNotification)
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Notification Details Modal */}
            {showModal && selectedNotification && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: '#E07A55', color: 'white' }}>
                                <h5 className="modal-title">
                                    Notification Details
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close btn-close-white" 
                                    onClick={closeNotificationModal}
                                ></button>
                            </div>
                            
                            <div className="modal-body">
                                {/* Notification Type */}
                                <div className="row mb-3">
                                    <div className="col-3">
                                        <strong>Type:</strong>
                                    </div>
                                    <div className="col-9">
                                        <span className={`badge ${
                                            selectedNotification.type === 'APPOINTMENT_NEW' ? 'bg-success' :
                                            selectedNotification.type === 'APPOINTMENT_CANCELLED' ? 'bg-danger' :
                                            selectedNotification.type === 'APPOINTMENT_RESCHEDULED' ? 'bg-warning' :
                                            'bg-info'
                                        }`}>
                                            {selectedNotification.type?.replace('APPOINTMENT_', '').replace('_', ' ')}
                                        </span>
                                    </div>
                                </div>

                                {/* Title */}
                                <div className="row mb-3">
                                    <div className="col-3">
                                        <strong>Title:</strong>
                                    </div>
                                    <div className="col-9">
                                        {selectedNotification.title}
                                    </div>
                                </div>

                                {/* Message */}
                                <div className="row mb-3">
                                    <div className="col-3">
                                        <strong>Message:</strong>
                                    </div>
                                    <div className="col-9">
                                        {selectedNotification.message}
                                    </div>
                                </div>

                                {/* Practitioner */}
                                {selectedNotification.practitioner_name && (
                                    <div className="row mb-3">
                                        <div className="col-3">
                                            <strong>Practitioner:</strong>
                                        </div>
                                        <div className="col-9">
                                            <span style={{ color: '#E07A55', fontWeight: '500' }}>
                                                Dr. {selectedNotification.practitioner_name}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Appointment Details */}
                                {selectedNotification.data && (
                                    <>
                                        <hr />
                                        <h6 className="mb-3" style={{ color: '#E07A55' }}>Appointment Details</h6>
                                        
                                        {selectedNotification.data.start_date && (
                                            <div className="row mb-2">
                                                <div className="col-3">
                                                    <strong>📅 Date:</strong>
                                                </div>
                                                <div className="col-9">
                                                    {new Date(selectedNotification.data.start_date).toLocaleDateString('en-US', {
                                                        weekday: 'long',
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {selectedNotification.data.start_time && (
                                            <div className="row mb-2">
                                                <div className="col-3">
                                                    <strong>⏰ Time:</strong>
                                                </div>
                                                <div className="col-9">
                                                    {selectedNotification.data.start_time}
                                                    {selectedNotification.data.end_time && 
                                                        ` - ${selectedNotification.data.end_time}`
                                                    }
                                                </div>
                                            </div>
                                        )}

                                        {selectedNotification.data.procedure && (
                                            <div className="row mb-2">
                                                <div className="col-3">
                                                    <strong>🩺 Procedure:</strong>
                                                </div>
                                                <div className="col-9">
                                                    {selectedNotification.data.procedure}
                                                </div>
                                            </div>
                                        )}

                                        {selectedNotification.data.location && (
                                            <div className="row mb-2">
                                                <div className="col-3">
                                                    <strong>🏥 Location:</strong>
                                                </div>
                                                <div className="col-9">
                                                    {selectedNotification.data.location}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Notification Metadata */}
                                <hr />
                                <h6 className="mb-3" style={{ color: '#E07A55' }}>Notification Info</h6>
                                
                                <div className="row mb-2">
                                    <div className="col-3">
                                        <strong>Received:</strong>
                                    </div>
                                    <div className="col-9">
                                        {new Date(selectedNotification.timestamp).toLocaleString()}
                                    </div>
                                </div>

                                <div className="row mb-2">
                                    <div className="col-3">
                                        <strong>Source:</strong>
                                    </div>
                                    <div className="col-9">
                                        {selectedNotification.source || 'Epic FHIR'}
                                    </div>
                                </div>

                                <div className="row mb-2">
                                    <div className="col-3">
                                        <strong>Status:</strong>
                                    </div>
                                    <div className="col-9">
                                        <span className={`badge ${
                                            selectedNotification.read ? 'bg-secondary' : 'bg-primary'
                                        }`}>
                                            {selectedNotification.read ? 'Read' : 'Unread'}
                                        </span>
                                        
                                        {selectedNotification.status && selectedNotification.status !== 'pending' && (
                                            <span className={`badge ms-2 ${
                                                selectedNotification.status === 'accepted' ? 'bg-success' : 'bg-danger'
                                            }`}>
                                                {selectedNotification.status === 'accepted' ? 'Accepted' : 'Rejected'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="modal-footer">
                                {/* Action buttons if needed */}
                                {selectedNotification.requiresAction && selectedNotification.status === 'pending' && (
                                    <>
                                        <button 
                                            type="button" 
                                            className="btn btn-success"
                                            onClick={() => {
                                                handleNotificationAction(selectedNotification.id, 'accepted');
                                                closeNotificationModal();
                                            }}
                                        >
                                            Accept
                                        </button>
                                        <button 
                                            type="button" 
                                            className="btn btn-danger"
                                            onClick={() => {
                                                handleNotificationAction(selectedNotification.id, 'rejected');
                                                closeNotificationModal();
                                            }}
                                        >
                                            Reject
                                        </button>
                                    </>
                                )}
                                
                                {!selectedNotification.read && (
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-primary"
                                        onClick={() => {
                                            markAsRead(selectedNotification.id);
                                            closeNotificationModal();
                                        }}
                                    >
                                        Mark as Read
                                    </button>
                                )}
                                
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={closeNotificationModal}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Notifications;